import {
  deleteMePreferenceV1,
  getMePreferencesV1,
  upsertMePreferenceV1,
} from '../../api/me-—-preferences/me-—-preferences';
import { updateMePersonalizedFashionInterestV1 } from '../../api/me-—-profile/me-—-profile';
import {
  AppSettingsContentResponseModel,
  MeDto,
  PreferenceCategoryContentResponseModel,
  PreferenceCategoryContentResponseModelContentType,
  PreferenceContentResponseModel,
  PreferenceContentResponseModelContentType,
  PreferenceDto,
  PreferenceSubcategoryContentResponseModel,
  PreferenceSubcategoryContentResponseModelContentType,
  UpsertPreferenceRequest,
} from '../../api/model';
import { getContents } from '../cms';

/**
 * Fetches all active preferences for the authenticated user.
 *
 * Returns preferences ordered by category and subcategory as defined by the API.
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param language - BCP 47 locale tag forwarded as `Accept-Language`.
 * @returns Array of `PreferenceDto` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated) or network error.
 */
export async function getUserPreferences(language?: string): Promise<PreferenceDto[] | undefined> {
  try {
    const response = await getMePreferencesV1({ headers: { 'Accept-Language': language ?? '' } });

    if (response.status === 200) {
      return response.data as PreferenceDto[];
    }

    console.warn(`getPreferences: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getPreferences: request failed:', error);
    return undefined;
  }
}

/**
 * Creates or updates a preference for the authenticated user.
 *
 * The API matches on `(categoryNodeKey, subcategoryNodeKey)` and overwrites on conflict. To store multiple
 * independent values within the same CMS subcategory, pass each item's own CMS
 * node ID as `subcategoryNodeKey` rather than the subcategory group ID.
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param request - Preference payload to create or update.
 * @param language - BCP 47 locale tag forwarded as `Accept-Language`.
 * @returns The saved `PreferenceDto` on HTTP 200.
 * @returns `undefined` on 400 (validation error), 401 (unauthenticated), or network error.
 */
export async function setPreference(
  request: UpsertPreferenceRequest,
  language?: string
): Promise<PreferenceDto | undefined> {
  try {
    const response = await upsertMePreferenceV1(request, { headers: { 'Accept-Language': language ?? '' } });

    if (response.status === 200) {
      return response.data as PreferenceDto;
    }

    console.warn(`setPreference: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('setPreference: request failed:', error);
    return undefined;
  }
}

/**
 * Soft-deletes a preference for the authenticated user.
 *
 * The record is marked inactive and retained for audit and CRM sync purposes —
 * it is not permanently removed. Matched by the preference's `publicGuid`.
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param id - The `publicGuid` of the preference to delete.
 * @param language - BCP 47 locale tag forwarded as `Accept-Language`.
 * @returns `true` on HTTP 204.
 * @returns `false` on 401 (unauthenticated), 404 (preference not found), or network error.
 */
export async function deletePreference(id: string, language?: string): Promise<boolean> {
  try {
    const response = await deleteMePreferenceV1(id, { headers: { 'Accept-Language': language ?? '' } });

    if (response.status === 204) {
      return true;
    }

    console.warn(`deletePreference: unexpected status ${response.status}`);
    return false;
  } catch (error) {
    console.warn('deletePreference: request failed:', error);
    return false;
  }
}

/**
 * Fetches all preference categories from the CMS.
 *
 * Queries the children of the `preferencesRepository` node defined in `appSettings`.
 * Returns every item cast to `PreferenceCategoryContentResponseModel` — ordered as the
 * CMS returns them (typically alphabetical or editorial order).
 *
 * @param appSettings - App settings page, used to read `preferencesRepository.id`.
 * @param culture - BCP 47 locale tag (e.g. `"en-GB"`) forwarded as `Accept-Language`.
 * @returns Array of `PreferenceCategoryContentResponseModel` on success.
 * @returns `undefined` when the repository ID is absent, on non-2xx response, or on network error.
 */
export async function getPreferencesCategories(
  appSettings: AppSettingsContentResponseModel,
  culture?: string
): Promise<PreferenceCategoryContentResponseModel[] | undefined> {
  try {
    const repositoryId = appSettings.properties?.preferencesRepository?.id;

    if (!repositoryId) {
      console.warn('getPreferencesCategories: preferencesRepository id is missing from appSettings');
      return undefined;
    }

    const result = await getContents(
      {
        fetch: `children:${repositoryId}`,
        filter: [`contentType:${PreferenceCategoryContentResponseModelContentType.preferenceCategory}`],
        skip: 0,
        take: 9999,
        sort: ['name:asc', 'sortOrder:asc'],
      },
      culture
    );

    return (result?.items ?? []) as PreferenceCategoryContentResponseModel[];
  } catch (error) {
    console.warn('getPreferencesCategories: request failed:', error);
    return undefined;
  }
}

/**
 * Fetches all subcategories for a given preference category from the CMS.
 *
 * Queries the children of the specified category node, filtered to
 * `preferenceSubcategory` content type. Results are ordered alphabetically
 * by name, with `sortOrder` as a secondary sort.
 *
 * @param categoryId - The CMS node ID of the parent preference category.
 * @param culture - BCP 47 locale tag (e.g. `"en-GB"`) forwarded as `Accept-Language`.
 * @returns Array of `PreferenceSubcategoryContentResponseModel` on success.
 * @returns `undefined` on non-2xx response or network error.
 */
export async function getPreferencesSubcategories(
  categoryId: string,
  culture?: string
): Promise<PreferenceSubcategoryContentResponseModel[] | undefined> {
  try {
    const result = await getContents(
      {
        fetch: `children:${categoryId}`,
        filter: [`contentType:${PreferenceSubcategoryContentResponseModelContentType.preferenceSubcategory}`],
        skip: 0,
        take: 99999,
        sort: ['sortOrder:asc', 'name:asc'],
      },
      culture
    );

    return (result?.items ?? []) as PreferenceSubcategoryContentResponseModel[];
  } catch (error) {
    console.warn('getPreferencesSubcategories: request failed:', error);
    return undefined;
  }
}

/**
 * Fetches CMS content items for a given preference subcategory.
 *
 * Queries the direct children of the specified subcategory node. Follows the same
 * drill-down pattern as `getPreferencesCategories` and `getPreferencesSubcategories`.
 *
 * @param subcategoryId - The CMS node ID of the parent preference subcategory.
 * @param culture - BCP 47 locale tag (e.g. `"en-GB"`) forwarded as `Accept-Language`.
 * @returns Array of `IApiContentResponseModel` on success.
 * @returns `undefined` on non-2xx response or network error.
 */
export async function getPreferencesBySubcategory(
  subcategoryId: string,
  culture?: string
): Promise<PreferenceContentResponseModel[] | undefined> {
  try {
    const result = await getContents(
      {
        fetch: `children:${subcategoryId}`,
        filter: [`contentType:${PreferenceContentResponseModelContentType.preference}`],
        skip: 0,
        take: 9999,
        sort: ['name:asc', 'sortOrder:asc'],
      },
      culture
    );

    return (result?.items ?? []) as PreferenceContentResponseModel[];
  } catch (error) {
    console.warn('getPreferencesBySubcategory: request failed:', error);
    return undefined;
  }
}

/**
 * Updates the personalized fashion interest field for the authenticated user.
 *
 * Sends a `PATCH /api/v1/me/personalized-fashion-interest`. The field is a
 * free-form string stored on the customer profile; pass `null` to clear it.
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param personalizedFashionInterest - Free-form text describing the user's fashion interest, or `null` to clear.
 * @param language - BCP 47 locale tag forwarded as `Accept-Language`.
 * @returns The updated `MeDto` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated), 404 (user not found), or network error.
 */
export async function updatePersonalizedFashionInterest(
  personalizedFashionInterest: string | null,
  language?: string
): Promise<MeDto | undefined> {
  try {
    const response = await updateMePersonalizedFashionInterestV1(
      { personalizedFashionInterest },
      { headers: { 'Accept-Language': language ?? '' } }
    );

    if (response.status === 200) {
      return response.data as MeDto;
    }

    console.warn(`updatePersonalizedFashionInterest: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('updatePersonalizedFashionInterest: request failed:', error);
    return undefined;
  }
}
